import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subject, zip } from 'rxjs';

import { DefaultsService } from 'src/app/core/providers';
import { NullableBinaryFilterInputComponent } from './filter-types/nullable-binary-filter-input.component';
import { FilterDefault } from 'src/app/core/models/filter-default.model';
import { FormBuilderConstructor, InputOption } from './filter-types/types';

const DEBUG = false;

export type ApiDataType = 'nullable-boolean';

export type NullableBooleanValue = true | false | null;

// Stubbing in hooks to be used to transform data before it is presented by the input, ie after the
// api result is loaded. A hook to transform the input's data before the submission happens.
// Not sure if we will need this, but what I don't want is various per form field transformations being configured
// all of the place.
type FilterConfigHooks = {
  onChange?: (value: any) => unknown;
  beforeSubmit?: (value: any) => unknown;
};

export type FilterConfig = {
  type: ApiDataType;
  component: any;
  apiProperty: string;
  localization: Record<string, string>;
  options: InputOption<unknown>[];
  hooks?: FilterConfigHooks;
} & FilterDefault;

export const DATA_TYPE_TO_COMPONENT_MAP: Map<ApiDataType, any> = new Map();
DATA_TYPE_TO_COMPONENT_MAP.set(
  'nullable-boolean',
  NullableBinaryFilterInputComponent
);

const CODE_NAME_TO_FILTER_CONFIG_MAP: Map<
  string,
  Partial<FilterConfig>
> = new Map();

CODE_NAME_TO_FILTER_CONFIG_MAP.set('mobile.rate.exclude-operated', {
  type: 'nullable-boolean',
  apiProperty: 'exclude_operated',
  localization: {
    button: 'main.filters.mobile.rate.exclude-operated.button',
  },
  options: [
    {
      label: 'main.filters.mobile.rate.exclude-operated.options.operated',
      value: 0,
    },
    {
      label: 'main.filters.mobile.rate.exclude-operated.options.non_operated',
      value: 1,
    },
    {
      label: 'main.filters.mobile.rate.exclude-operated.options.include_both',
      value: null,
    },
  ],
  hooks: {
    beforeSubmit: (value: boolean | null) => {
      if (value === null) {
        return [];
      }
      return [value];
    },
  },
});

export const LOCAL_STORAGE_KEY = 'mqafilters';

@Injectable()
export class FiltersService {
  protected _form: FormGroup;
  protected _filters: Map<string, FilterConfig> = new Map();
  public filtersChanged: Subject<void> = new Subject<void>();

  constructor(
    protected translateService: TranslateService,
    protected defaultsService: DefaultsService,
    protected fb: FormBuilder
  ) {
    // Zipping defaults with translate service so we can be assured
    // that translations are available.
    zip(
      this.translateService.onDefaultLangChange,
      this.defaultsService.filterDefaultsLoaded
    ).subscribe((result: any) => {
      this.initialize(result[1]);
    });
  }

  public get filters(): Map<string, FilterConfig> {
    return this._filters;
  }

  public getFilter(filterName: string): FilterConfig {
    const filter = this.filters.get(filterName) ?? null;
    if (!filter) {
      throw new Error(`${filterName} wasn't found.`);
    }
    return filter;
  }

  public get filterList(): FilterConfig[] {
    return Array.from(this._filters.values());
  }

  public get appliedFilters(): string {
    if (!this._form) {
      return '';
    }
    return JSON.stringify(this._form.value);
  }

  // TODO: I don't think this is used?
  public get fields(): FormArray {
    return this._form.get('fields') as FormArray;
  }

  public get form(): FormGroup {
    return this._form;
  }

  public addField(name: string) {
    const filter = this.getFilter(name);

    if (!this.form.contains(name)) {
      const cls: FormBuilderConstructor<any> = filter.component;
      const control: FormControl = cls.buildFormControl(filter.DefaultValue);
      this.form.addControl(name, control);
      return control;
    } else {
      const control = this.form.controls[name];
      return control;
    }
    return null;
  }

  // Remove the stored values and clear the form if the client is switched.
  public clearCache() {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({}));
  }

  private mapFilters(
    defaults: FilterDefault[],
    configs: Map<string, Partial<FilterConfig>>,
    dataTypeToComponentMap: Map<ApiDataType, any>
  ) {
    const storedValuesStr = localStorage.getItem(LOCAL_STORAGE_KEY);
    const storedValuesMap = storedValuesStr ? JSON.parse(storedValuesStr) : {};

    for (const key of configs.keys()) {
      const filter = defaults.find(
        (filterDefault: FilterDefault) => filterDefault.CodeName === key
      );
      if (filter) {
        // this._values.set(filter.CodeName, filter.DefaultValue);
        const config = configs.get(key);
        if (config) {
          if (!config.type) {
            throw new Error('Filter config type is not set.');
          }
          const component: any = dataTypeToComponentMap.get(config?.type);
          const filterConfig = this.translateConfig({
            ...config,
            ...filter,
            component,
          } as FilterConfig);

          if (storedValuesMap[key] !== undefined) {
            filterConfig.DefaultValue = storedValuesMap[key];
          }

          this.filters.set(key, filterConfig);
        }
      }
    }
  }

  private initialize(filterDefaults: FilterDefault[]) {
    this._form = this.fb.group({});

    if (DEBUG) {
      console.log('filters.service filtersLoaded ', filterDefaults);
    }

    this.mapFilters(
      filterDefaults,
      CODE_NAME_TO_FILTER_CONFIG_MAP,
      DATA_TYPE_TO_COMPONENT_MAP
    );

    this._form.valueChanges.subscribe((result) => {
      if (DEBUG) {
        console.log('filters.service form valueChanges ', result);
      }
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(this.form.value));
      this.filtersChanged.next();
    });
    this.initializeForm();
  }

  private initializeForm() {
    const storedValuesStr = localStorage.getItem(LOCAL_STORAGE_KEY);
    const storedValuesMap = storedValuesStr ? JSON.parse(storedValuesStr) : {};
    const keys = Object.keys(storedValuesMap);
    keys.forEach((key) => this.addField(key));
  }

  private translateConfig(item: FilterConfig): FilterConfig {
    if (DEBUG) {
      console.log('filters.service.translateConfig  ', item);
    }
    const { localization } = item;
    const result: Record<string, string> = {};

    // translate any localization strings.
    const keys = Object.keys(localization);
    keys.forEach((key) => {
      result[key] = this.translateService.instant(localization[key]);
    });

    item.localization = result;

    // translate all the options.
    item.options = item.options.map((option) => {
      option.label = this.translateService.instant(option.label);
      return option;
    });
    if (DEBUG) {
      console.log('       after translation', item);
    }
    return item;
  }
}
